import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import SendIcon from '@mui/icons-material/Send'

import { AutoCoderEntity } from '../../../types/gotypes'
import PlatformAttribution from '../../social/PlatformAttribution'
import {
  DEFAULT_ENTITY_SORT,
  EntitySort,
  EntitySortKey,
  entityDisplayName,
  formatPublishedDate,
  sortEntities,
  useEntityBrowser,
} from '../../../hooks/useEntityBrowser'
import { useJobReceipts } from '../../../hooks/useJobReceipts'
import { formatImportTimestamp } from '../../../utils/importBatches'
import { formatMetricValue, FormatType } from '../../social-media/metrics-config'
import EntityDetailView from './EntityDetailView'

/**
 * The job's ENTITY browser — the videos, posts and other things its records are
 * about, listed independently of any pipeline.
 *
 * Deliberately generic in its chrome ("entities", not "videos"): the same table
 * serves a TikTok job and a survey one, and platform words belong in the rows,
 * where they are true, not in the headings.
 *
 * The receipt filter reads "discovered by", not "touched by": an entity's
 * `import_batch_id` is first-wins, so a later tick that finds new comments on a
 * known video leaves that video filed under the fetch that FIRST saw it.
 */

interface EntitiesTableProps {
  customer: string
  job: string
  /** False while the containing tab is not showing. */
  active?: boolean
  /**
   * Opens the send-to-pipeline flow for one entity. The action renders only
   * when a handler is supplied, so the browser never shows a door that leads
   * nowhere.
   */
  onSendToPipeline?: (entity: AutoCoderEntity) => void
  /** Only entities from these platforms; all of the job's entities without it. */
  platforms?: string[]
}

/** How many receipts the "discovered by" picker offers — the recent ones. */
const RECEIPT_FILTER_OPTIONS = 100

const EntitiesTable: React.FC<EntitiesTableProps> = ({
  customer,
  job,
  active = true,
  onSendToPipeline,
  platforms,
}) => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [importBatchId, setImportBatchId] = useState('')
  // Typed vs committed: a request per keystroke over a job's whole entity set
  // is not a search, it is a load test. Enter (or the icon) commits.
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<EntitySort>(DEFAULT_ENTITY_SORT)
  const [detail, setDetail] = useState<AutoCoderEntity | null>(null)

  const { entities, total, metricsByEntity, metricColumns, loading, isFetching, error, refetch } =
    useEntityBrowser({
      customer,
      job,
      filters: { importBatchId: importBatchId || undefined, search: search || undefined, platforms },
      page,
      rowsPerPage,
      enabled: active,
    })

  // The receipts the filter offers. One cached page of the same ledger the
  // Receipts view shows, not a second source of truth.
  const receipts = useJobReceipts(customer, job, 0, RECEIPT_FILTER_OPTIONS, active)

  const rows = useMemo(
    () => sortEntities(entities, sort, metricsByEntity),
    [entities, sort, metricsByEntity],
  )

  const toggleSort = (key: EntitySortKey) => {
    setSort(current =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'published' ? 'desc' : 'asc' },
    )
  }

  const sortableHeader = (key: EntitySortKey, label: string, align: 'left' | 'right' = 'left') => (
    <TableCell align={align} sortDirection={sort.key === key ? sort.direction : false}>
      <TableSortLabel
        active={sort.key === key}
        direction={sort.key === key ? sort.direction : 'asc'}
        onClick={() => toggleSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  )

  const columnCount = 4 + metricColumns.length + (onSendToPipeline ? 1 : 0)
  // Sorting reorders the rows this page holds. Metrics arrive per page (one
  // grouped request for the ids on screen), so there is no honest way to sort
  // the whole job by them here — and saying so is better than implying it.
  const sortIsPageLocal = total > rows.length

  return (
    <Box sx={{ p: 3, overflow: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1">Entities</Typography>
          <Typography variant="body2" color="text.secondary">
            The videos, posts and other things this job&apos;s records are about.
            {total ? ` ${total.toLocaleString()} in scope.` : ''}
          </Typography>
        </Box>
        <Tooltip title="Refresh now">
          <span>
            <IconButton color="info" onClick={refetch} disabled={isFetching} aria-label="Refresh now">
              {isFetching ? <CircularProgress size={20} color="info" /> : <RefreshIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          select
          size="small"
          label="Discovered by"
          value={importBatchId}
          onChange={e => {
            setImportBatchId(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 320 }}
          helperText="The fetch that FIRST saw the entity — not every fetch that has touched it."
        >
          <MenuItem value="">Any fetch</MenuItem>
          {(receipts.data?.batches || []).map(batch => (
            <MenuItem key={batch.id} value={batch.id}>
              {formatImportTimestamp(batch.requestedAt)} · {batch.source}
              {batch.channel ? ` / ${batch.channel}` : ''}
            </MenuItem>
          ))}
        </TextField>

        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault()
            setSearch(searchInput.trim())
            setPage(0)
          }}
        >
          <TextField
            size="small"
            label="Search name or URL"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            sx={{ minWidth: 280 }}
            helperText="Press Enter to search."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {search ? (
                    <IconButton
                      size="small"
                      aria-label="Clear the search"
                      onClick={() => {
                        setSearchInput('')
                        setSearch('')
                        setPage(0)
                      }}
                    >
                      <ClearIcon fontSize="inherit" />
                    </IconButton>
                  ) : (
                    <SearchIcon fontSize="small" color="disabled" />
                  )}
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress color="info" />
        </Box>
      )}

      {!loading && rows.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {importBatchId || search
              ? 'No entities match this filter.'
              : 'This job has no entities yet. They are created by the connectors that fetch records — a CSV upload has none.'}
          </Typography>
        </Paper>
      )}

      {rows.length > 0 && (
        <Paper elevation={1}>
          <TableContainer>
            <Table size="small" data-testid="autocoder-entities-table">
              <TableHead>
                <TableRow>
                  {sortableHeader('name', 'Entity')}
                  {sortableHeader('platform', 'Platform')}
                  {sortableHeader('kind', 'Kind')}
                  {sortableHeader('published', 'Published')}
                  {metricColumns.map(metric => (
                    <React.Fragment key={metric.metric}>
                      {sortableHeader(`metric:${metric.metric}`, metric.displayName, 'right')}
                    </React.Fragment>
                  ))}
                  {onSendToPipeline && <TableCell align="right">Send</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(entity => (
                  <TableRow
                    key={entity.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setDetail(entity)}
                    // The entity id, not the position: this table is sortable,
                    // so the row order is a user preference and index-addressed
                    // rows would point at a different entity per click.
                    data-testid={`autocoder-entity-row-${entity.id}`}
                  >
                    <TableCell sx={{ maxWidth: 420 }}>
                      <Typography variant="body2" noWrap>
                        {entityDisplayName(entity)}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {entity.externalId}
                        </Typography>
                        {entity.url && !entity.platform && (
                          <Link
                            href={entity.url}
                            target="_blank"
                            rel="noreferrer"
                            variant="caption"
                            onClick={e => e.stopPropagation()}
                            sx={{ display: 'inline-flex', alignItems: 'center' }}
                            aria-label="Open this entity on its platform"
                          >
                            <OpenInNewIcon fontSize="inherit" />
                          </Link>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {entity.platform ? (
                        <PlatformAttribution platform={entity.platform} url={entity.url} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {entity.kind || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {formatPublishedDate(entity.publishedDate)}
                      </Typography>
                    </TableCell>
                    {metricColumns.map(column => {
                      const value = metricsByEntity[entity.id]?.[column.metric]
                      return (
                        <TableCell key={column.metric} align="right">
                          <Typography variant="body2">
                            {/* A metric this entity has no row for is a gap,
                                never a zero. */}
                            {value
                              ? formatMetricValue(
                                  value.value,
                                  (value.format || column.format) as FormatType,
                                )
                              : '—'}
                          </Typography>
                        </TableCell>
                      )
                    })}
                    {onSendToPipeline && (
                      <TableCell align="right">
                        <Tooltip title="Send this entity's records to a pipeline">
                          <IconButton
                            size="small"
                            color="info"
                            aria-label="Send this entity's records to a pipeline"
                            data-testid={`autocoder-entity-send-${entity.id}`}
                            onClick={e => {
                              e.stopPropagation()
                              onSendToPipeline(entity)
                            }}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {sortIsPageLocal && (
                  <TableRow>
                    <TableCell colSpan={columnCount} sx={{ borderBottom: 'none' }}>
                      <Typography variant="caption" color="text.secondary">
                        Sorting orders the {rows.length.toLocaleString()} entities on this page.
                        Paging follows the job&apos;s own order — newest published first.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
          />
        </Paper>
      )}

      <EntityDetailView
        customer={customer}
        job={job}
        entity={detail}
        onClose={() => setDetail(null)}
        onSendToPipeline={onSendToPipeline}
      />
    </Box>
  )
}

export default EntitiesTable
